import { useState, useEffect } from 'react';
import nfcService, { NFCPaymentData } from '@/services/nfcService';

interface UseNFCReturn {
  isSupported: boolean;
  isEnabled: boolean;
  isWriting: boolean;
  isReading: boolean;
  error: string | null;
  writePaymentData: (data: NFCPaymentData) => Promise<void>;
  readPaymentData: () => Promise<NFCPaymentData>;
  requestPermission: () => Promise<boolean>;
}

export const useNFC = (): UseNFCReturn => {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkNFCSupport = async () => {
      const supported = nfcService.isNFCSupported();
      setIsSupported(supported);
      
      if (supported) {
        const enabled = await nfcService.isNFCEnabled();
        setIsEnabled(enabled);
      }
    };

    checkNFCSupport();
  }, []);

  const writePaymentData = async (data: NFCPaymentData): Promise<void> => {
    setError(null);
    setIsWriting(true);
    
    try {
      await nfcService.writePaymentData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to write NFC data');
      throw err;
    } finally {
      setIsWriting(false);
    }
  };

  const readPaymentData = async (): Promise<NFCPaymentData> => {
    setError(null);
    setIsReading(true);
    
    try {
      return await nfcService.readPaymentData();
    } catch (err: any) {
      setError(err.message || 'Failed to read NFC data');
      throw err;
    } finally {
      setIsReading(false);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    setError(null);
    
    try {
      const granted = await nfcService.requestNFCPermission();
      if (granted) {
        setIsEnabled(true);
      }
      return granted;
    } catch (err: any) {
      setError(err.message || 'Failed to request NFC permission');
      return false;
    }
  };

  return {
    isSupported,
    isEnabled,
    isWriting,
    isReading,
    error,
    writePaymentData,
    readPaymentData,
    requestPermission
  };
}; 